/**
 * @bos/portal3 — the sign-in page (route `/login`, inside <AuthLayout>, wrapped in
 * <RedirectIfAuthed>).
 *
 * Wired to `auth.login`: on success the session cookie is set server-side and the user
 * is sent to the dashboard (or back to the protected page they were headed for). Wrong
 * credentials surface the server's clean "Invalid email or password." message.
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { PATHS } from '../routes';
import { useAuth, authErrorMessage } from '../auth/AuthContext';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? PATHS.dashboard;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(authErrorMessage(err));
      setBusy(false);
    }
  }

  return (
    <section className="authcard">
      <h1 className="authcard__title">Welcome back</h1>
      <p className="authcard__lede">Sign in to your Sorvel workspace.</p>
      <form className="form" onSubmit={onSubmit} aria-label="Sign in">
        {error && (
          <p className="form__error" role="alert">
            {error}
          </p>
        )}
        <label className="form__field">
          <span className="form__label">Email</span>
          <input
            className="form__input"
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
          />
        </label>
        <label className="form__field">
          <span className="form__label">Password</span>
          <input
            className="form__input"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
          />
        </label>
        <button className="btn btn--primary btn--block" type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <p className="authcard__switch">
        New to Sorvel? <Link to={PATHS.register}>Create an account</Link>
      </p>
    </section>
  );
}
